import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const EMPTY_ARRAY = [];

const PurchaseOrderModal = ({ supplier, catalogue, isOpen, onClose, onCreated, order = null, initialItems = EMPTY_ARRAY }) => {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [reference, setReference] = useState('');
    const [supplierReference, setSupplierReference] = useState('');
    const [erpReference, setErpReference] = useState('');
    const [expectedDate, setExpectedDate] = useState('');
    const [notes, setNotes] = useState('');
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [email, setEmail] = useState('');
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

    // Initialisation
    useEffect(() => {
        if (isOpen) {
            if (order) {
                // Mode Édition
                setReference(order.reference || '');
                setSupplierReference(order.supplierReference || '');
                setErpReference(order.erpReference || '');
                setNotes(order.notes || '');
                setExpectedDate(order.expectedDate ? order.expectedDate.split('T')[0] : '');

                // Mapper les items existants
                const existingItems = (order.items || []).map(item => ({
                    productId: item.productId,
                    catalogItemId: item.catalogItemId,
                    name: item.product?.name || item.catalogItem?.name || 'Inconnu',
                    sku: item.product?.sku || item.catalogItem?.sku || '',
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    leadTime: 0
                }));
                setItems(existingItems);
            } else {
                // Mode Création
                setReference('(Générée par le système)');
                setSupplierReference('');
                setErpReference('');
                setNotes('');

                if (initialItems && initialItems.length > 0) {
                    const mappedInitialItems = initialItems.map(p => ({
                        productId: p.isFromInventory ? p.id : null,
                        catalogItemId: !p.isFromInventory ? p.id : null,
                        name: p.name,
                        sku: p.sku,
                        quantity: 1,
                        unitPrice: (p.isFromInventory ? p.ProductSupplier?.purchasePrice : p.purchasePrice) || 0,
                        leadTime: (p.isFromInventory ? p.ProductSupplier?.leadTime : p.leadTime) || 0
                    }));
                    setItems(mappedInitialItems);
                    recalculateExpectedDate(mappedInitialItems, supplier);
                } else {
                    setItems([]);
                    // Initialiser avec le délai moyen du fournisseur
                    if (supplier?.avgDeliveryTime) {
                        const days = parseInt(supplier.avgDeliveryTime);
                        if (!isNaN(days)) {
                            const date = new Date();
                            date.setDate(date.getDate() + days);
                            setExpectedDate(date.toISOString().split('T')[0]);
                        } else {
                            setExpectedDate('');
                        }
                    } else {
                        setExpectedDate('');
                    }
                }
            }
            setSearchTerm('');
            setEmail(supplier?.contactEmail || '');
        }
    }, [isOpen, supplier?.id, order?.id]); // Only re-run if modal opens or supplier/order changes

    const totalHT = useMemo(() => {
        return items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    }, [items]);

    const recalculateExpectedDate = (currentItems, currentSupplier = supplier) => {
        if (currentItems.length === 0) {
            if (currentSupplier?.avgDeliveryTime) {
                const days = parseInt(currentSupplier.avgDeliveryTime);
                if (!isNaN(days)) {
                    const date = new Date();
                    date.setDate(date.getDate() + days);
                    setExpectedDate(date.toISOString().split('T')[0]);
                    return;
                }
            }
            setExpectedDate('');
            return;
        }

        const leadTimes = currentItems.map(i => i.leadTime).filter(lt => lt > 0);
        const maxItemLeadTime = leadTimes.length > 0 ? Math.max(...leadTimes) : (parseInt(currentSupplier?.avgDeliveryTime) || 0);

        if (maxItemLeadTime > 0) {
            const date = new Date();
            date.setDate(date.getDate() + maxItemLeadTime);
            setExpectedDate(date.toISOString().split('T')[0]);
        }
    };

    const addItem = (product) => {
        const isFromInventory = product.isFromInventory;
        if (items.some(i => isFromInventory ? i.productId === product.id : i.catalogItemId === product.id)) return;

        const newItems = [...items, {
            productId: isFromInventory ? product.id : null,
            catalogItemId: !isFromInventory ? product.id : null,
            name: product.name,
            sku: product.sku,
            quantity: 1,
            unitPrice: (isFromInventory ? product.ProductSupplier?.purchasePrice : product.purchasePrice) || 0,
            leadTime: (isFromInventory ? product.ProductSupplier?.leadTime : product.leadTime) || 0
        }];
        setItems(newItems);
        recalculateExpectedDate(newItems);
    };

    const removeItem = (item) => {
        const newItems = items.filter(i => {
            if (item.productId) return i.productId !== item.productId;
            return i.catalogItemId !== item.catalogItemId;
        });
        setItems(newItems);
        recalculateExpectedDate(newItems);
    };

    const updateItem = (item, field, value) => {
        setItems(items.map(i => {
            const isMatch = item.productId
                ? (i.productId === item.productId)
                : (i.catalogItemId === item.catalogItemId);
            if (isMatch) {
                return { ...i, [field]: value };
            }
            return i;
        }));
    };

    const filteredCatalogue = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return (catalogue || []).filter(p =>
            p.name.toLowerCase().includes(term) ||
            p.sku.toLowerCase().includes(term) ||
            (p.isFromInventory ? p.ProductSupplier?.supplierRef : p.supplierRef)?.toLowerCase().includes(term)
        );
    }, [catalogue, searchTerm]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (items.length === 0) {
            showToast("Ajoutez au moins un produit", "error");
            return;
        }

        const today = new Date().toISOString().split('T')[0];
        if (expectedDate && expectedDate < today) {
            showToast("La date de livraison ne peut pas être dans le passé", "error");
            return;
        }

        try {
            const url = order ? `/api/purchase-orders/${order.id}` : '/api/purchase-orders';
            const method = order ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    supplierId: supplier.id,
                    companyId: user.companyId,
                    reference: order ? reference : undefined, // Let backend generate if new
                    supplierReference,
                    erpReference,
                    expectedDate: expectedDate || null,
                    notes,
                    items: items.map(i => ({
                        productId: i.productId || null,
                        catalogItemId: i.catalogItemId || null,
                        quantity: i.quantity,
                        unitPrice: i.unitPrice,
                        totalPrice: (i.quantity * i.unitPrice)
                    })),
                    userId: user.id
                })
            });

            if (res.ok) {
                showToast(order ? "Commande mise à jour" : "Commande créée", "success");
                onCreated();
                onClose();
            } else {
                showToast("Erreur lors de l'enregistrement", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleDelete = () => {
        showConfirm({
            title: 'Supprimer la commande ?',
            message: 'Voulez-vous vraiment supprimer ce brouillon ? Cette action est irréversible.',
            type: 'danger',
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/purchase-orders/${order.id}`, { method: 'DELETE' });
                    if (res.ok) {
                        showToast("Commande supprimée", "success");
                        onCreated();
                        onClose();
                    } else {
                        showToast("Erreur lors de la suppression", "error");
                    }
                } catch (err) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };

    const handleSendEmail = async () => {
        try {
            const res = await fetch(`/api/purchase-orders/${order.id}/send-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, userId: user.id })
            });

            if (res.ok) {
                showToast("Commande validée et envoyée", "success");
                onCreated();
                onClose();
            } else {
                const data = await res.json();
                showToast(data.error || "Erreur lors de l'envoi", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        } finally {
            setIsEmailModalOpen(false);
        }
    };

    const francoMissing = supplier?.francoPort && totalHT < supplier.francoPort ? supplier.francoPort - totalHT : 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-6xl flex flex-col max-h-[95vh] border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">

                {/* Premium Header with Gradient */}
                <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                    <div className="relative z-10 flex justify-between items-center">
                        <div className="flex gap-4 items-center">
                            <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center shadow-inner">
                                <span className="material-symbols-outlined text-primary text-[28px]">
                                    {order ? 'edit_document' : 'add_shopping_cart'}
                                </span>
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                                    {order ? 'Modifier la commande' : "Nouvelle commande d'achat"}
                                </h2>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">
                                    Fournisseur : <span className="text-primary">{supplier?.name}</span> • {items.length} références
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {order && order.status === 'Brouillon' && (
                                <button
                                    onClick={handleDelete}
                                    className="size-10 flex items-center justify-center rounded-xl text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/10 dark:hover:bg-red-900/20 transition-all shadow-sm"
                                    title="Supprimer le brouillon"
                                >
                                    <span className="material-symbols-outlined text-[20px]">delete_forever</span>
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row divide-x divide-slate-100 dark:divide-slate-800">
                    {/* Left Panel: Form and Ordered Items */}
                    <div className="flex-1 flex flex-col min-w-0">
                        {/* Upper: Fixed Form Fields */}
                        <div className="p-8 pb-6 space-y-6 shrink-0 border-b border-slate-100 dark:border-slate-800/50 bg-slate-50/30 dark:bg-slate-800/10">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Réf. Système</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-slate-400 text-[18px]">terminal</span>
                                        </div>
                                        <input
                                            type="text"
                                            disabled
                                            className="w-full h-11 pl-14 pr-4 bg-slate-100/50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700 rounded-xl outline-none text-sm font-black text-slate-400 cursor-not-allowed italic"
                                            value={reference}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Livraison prévue</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-primary/10 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-colors">
                                            <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                                        </div>
                                        <input
                                            type="date"
                                            min={new Date().toISOString().split('T')[0]}
                                            className="w-full h-11 pl-14 pr-4 bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl outline-none focus:border-primary transition-all text-sm font-bold"
                                            value={expectedDate}
                                            onChange={e => {
                                                const val = e.target.value;
                                                const today = new Date().toISOString().split('T')[0];
                                                if (val && val < today) {
                                                    showToast("La date ne peut pas être dans le passé", "warning");
                                                    setExpectedDate(today);
                                                } else {
                                                    setExpectedDate(val);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Réf. ERP (Interne)</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-colors">
                                            <span className="material-symbols-outlined text-[18px]">tag</span>
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full h-11 pl-14 pr-4 bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl outline-none focus:border-primary transition-all text-sm font-mono font-bold"
                                            value={erpReference}
                                            onChange={e => setErpReference(e.target.value)}
                                            placeholder="Ex: ERP-2026-X"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Réf. Bon de Commande Fournisseur</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-colors">
                                            <span className="material-symbols-outlined text-[18px]">description</span>
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full h-11 pl-14 pr-4 bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl outline-none focus:border-primary transition-all text-sm font-mono font-bold"
                                            value={supplierReference}
                                            onChange={e => setSupplierReference(e.target.value)}
                                            placeholder="Le numéro de commande de votre fournisseur..."
                                        />
                                    </div>
                                </div>

                                <div className="col-span-2 space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Notes & Instructions</label>
                                    <textarea
                                        className="w-full h-20 px-4 py-3 bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl outline-none focus:border-primary transition-all text-sm resize-none font-medium"
                                        rows="2"
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                        placeholder="Instructions particulières pour cette commande..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Lower: Scrollable Items List */}
                        <div className="p-8 pt-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
                            <div className="flex items-center justify-between">
                                <h4 className="font-black text-slate-900 dark:text-white flex items-center gap-3 text-sm uppercase tracking-widest">
                                    <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-primary text-[20px]">list_alt</span>
                                    </div>
                                    Lignes de commande
                                    <span className="size-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-500">{items.length}</span>
                                </h4>
                            </div>

                            {items.length === 0 ? (
                                <div className="p-16 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2rem] bg-slate-50/50 dark:bg-transparent">
                                    <div className="size-16 rounded-2xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 flex items-center justify-center mx-auto mb-4">
                                        <span className="material-symbols-outlined text-3xl text-slate-300">shopping_basket</span>
                                    </div>
                                    <p className="text-sm font-bold text-slate-400 italic">Veuillez ajouter des produits depuis le catalogue à droite</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {items.map((item, idx) => (
                                        <div key={item.productId || `c-${item.catalogItemId}-${idx}`} className="flex flex-wrap items-center gap-4 p-5 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm hover:border-primary/30 hover:shadow-md transition-all group">
                                            <div className="flex-1 min-w-[200px]">
                                                <p className="text-sm font-black text-slate-900 dark:text-white leading-tight mb-1 group-hover:text-primary transition-colors">{item.name}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[9px] font-black text-slate-500 uppercase tracking-widest">{item.sku}</span>
                                                </div>
                                            </div>
                                            <div className="w-24">
                                                <label className="text-[9px] uppercase font-black text-slate-400 tracking-widest block mb-1.5">Quantité</label>
                                                <div className="relative group/input">
                                                    <input
                                                        type="number"
                                                        disabled={['Envoyée', 'Livrée'].includes(order?.status)}
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={e => updateItem(item, 'quantity', parseInt(e.target.value) || 1)}
                                                        className={`w-full h-9 px-3 rounded-lg border-2 outline-none text-sm font-black text-center transition-all ${['Envoyée', 'Livrée'].includes(order?.status) ? 'bg-slate-50/50 border-transparent text-slate-400 cursor-not-allowed' : 'bg-slate-50 dark:bg-slate-900 border-slate-50 dark:border-slate-700 focus:border-primary focus:bg-white'}`}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-28 text-right">
                                                <label className="text-[9px] uppercase font-black text-slate-400 tracking-widest block mb-1.5">PU HT</label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        disabled={['Envoyée', 'Livrée'].includes(order?.status)}
                                                        value={item.unitPrice}
                                                        onChange={e => {
                                                            const val = e.target.value.replace(',', '.');
                                                            updateItem(item, 'unitPrice', val);
                                                        }}
                                                        className={`w-full h-9 px-3 rounded-lg border-2 outline-none text-sm font-mono font-black text-right transition-all ${['Envoyée', 'Livrée'].includes(order?.status) ? 'bg-slate-50/50 border-transparent text-slate-400 cursor-not-allowed' : 'bg-slate-50 dark:bg-slate-900 border-slate-50 dark:border-slate-700 focus:border-primary focus:bg-white'}`}
                                                    />
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-black pointer-events-none opacity-30">€</span>
                                                </div>
                                            </div>
                                            <div className="w-28 text-right">
                                                <label className="text-[9px] uppercase font-black text-slate-400 tracking-widest block mb-1.5">Total HT</label>
                                                <p className="text-sm font-black text-slate-900 dark:text-white font-mono bg-slate-50 dark:bg-slate-900/50 h-9 px-3 flex items-center justify-end rounded-lg">
                                                    {(item.quantity * item.unitPrice).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                                                </p>
                                            </div>
                                            {order?.status !== 'Envoyée' && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(item)}
                                                    className="size-9 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel: Catalogue with Search */}
                    <div className="w-full md:w-96 shrink-0 bg-slate-50/50 dark:bg-slate-900/20 flex flex-col border-l border-slate-100 dark:border-slate-800">
                        <div className="p-8 pb-4">
                            <h4 className="font-black text-slate-900 dark:text-white mb-6 flex justify-between items-center text-[10px] uppercase tracking-[0.2em]">
                                Catalogue Fournisseur
                                <span className="px-2 py-0.5 bg-primary/10 rounded-full font-black text-primary">{filteredCatalogue.length}</span>
                            </h4>
                            <div className="relative group">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">search</span>
                                <input
                                    type="text"
                                    placeholder="Rechercher un produit..."
                                    className="w-full pl-10 pr-4 h-11 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-xl text-sm font-medium outline-none focus:border-primary transition-all shadow-sm"
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 pb-8 space-y-3 custom-scrollbar">
                            {filteredCatalogue.length === 0 ? (
                                <div className="py-20 text-center">
                                    <div className="size-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4 opacity-50">
                                        <span className="material-symbols-outlined text-3xl text-slate-400">inventory_2</span>
                                    </div>
                                    <p className="text-xs text-slate-400 font-bold italic tracking-wide">Aucun produit trouvé</p>
                                </div>
                            ) : filteredCatalogue.map(p => {
                                const inCart = items.some(i =>
                                    p.isFromInventory ? i.productId === p.id : i.catalogItemId === p.id
                                );
                                return (
                                    <button
                                        key={p.isFromInventory ? `p-${p.id}` : `c-${p.id}`}
                                        type="button"
                                        onClick={() => addItem(p)}
                                        disabled={inCart || ['Envoyée', 'Livrée'].includes(order?.status)}
                                        className={`w-full text-left p-4 rounded-2xl border-2 transition-all flex justify-between items-start gap-4 group cursor-pointer
                                            ${inCart
                                                ? 'bg-primary/5 border-primary/20 cursor-not-allowed'
                                                : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-800 hover:border-primary hover:shadow-xl hover:-translate-y-1'
                                            }`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-col gap-1">
                                                <p className="font-black text-[13px] leading-tight text-slate-900 dark:text-white group-hover:text-primary transition-colors truncate">{p.name}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-black font-mono text-slate-400 bg-slate-50 dark:bg-slate-900 px-1.5 py-0.5 rounded leading-none">{p.sku}</span>
                                                    {!p.isFromInventory && <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[8px] font-black uppercase tracking-widest">Master</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="font-black text-primary font-mono text-sm">{Number((p.isFromInventory ? p.ProductSupplier?.purchasePrice : p.purchasePrice) || 0).toFixed(2)}€</p>
                                            {inCart ? (
                                                <div className="flex items-center gap-1 text-[9px] text-primary font-black uppercase tracking-widest mt-2">
                                                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                                    OK
                                                </div>
                                            ) : (
                                                <div className="size-6 rounded-lg bg-slate-50 dark:bg-slate-900 flex items-center justify-center mt-2 group-hover:bg-primary group-hover:text-white transition-all">
                                                    <span className="material-symbols-outlined text-[16px]">add</span>
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Footer: Summary & Actions */}
                <div className="px-10 py-8 bg-slate-50 dark:bg-slate-950/50 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-8 shrink-0">
                    <div className="flex items-center gap-10">
                        <div>
                            <div className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-2">Total de la commande</div>
                            <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-black text-slate-900 dark:text-white font-mono">{totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</span>
                                <span className="text-base font-black text-slate-400 font-mono">€ HT</span>
                            </div>
                        </div>
                        {supplier?.francoPort && (
                            <div className={`px-6 py-3 rounded-2xl border-2 flex flex-col gap-1 transition-all duration-500 ${francoMissing > 0 ? 'bg-white dark:bg-slate-900 border-amber-200 shadow-xl shadow-amber-500/5' : 'bg-emerald-500 text-white border-emerald-400 shadow-xl shadow-emerald-500/20'}`}>
                                <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-[0.15em]">
                                    <span className="material-symbols-outlined text-[16px]">
                                        {francoMissing > 0 ? 'exclamation' : 'local_shipping'}
                                    </span>
                                    Objectif Franco ({supplier.francoPort}€)
                                </div>
                                <div className="flex items-baseline gap-1">
                                    {francoMissing > 0 ? (
                                        <>
                                            <span className="text-lg font-black font-mono text-amber-600">-{francoMissing.toFixed(2)}</span>
                                            <span className="text-[10px] font-black text-amber-500 font-mono">€</span>
                                        </>
                                    ) : (
                                        <span className="text-lg font-black uppercase tracking-wider">Atteint !</span>
                                    )}
                                </div>
                                <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full mt-1 overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-1000 ${francoMissing > 0 ? 'bg-amber-500' : 'bg-white'}`}
                                        style={{ width: `${Math.min(100, (totalHT / supplier.francoPort) * 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-8 py-3.5 rounded-xl text-slate-500 font-black hover:bg-slate-200 dark:hover:bg-slate-800 transition-all uppercase text-[10px] tracking-[0.2em]"
                        >
                            Annuler
                        </button>
                        {order && (order.status === 'Brouillon' || order.status === 'Envoyée' || order.status === 'Livrée') && (
                            <button
                                type="button"
                                onClick={() => setIsEmailModalOpen(true)}
                                className="flex items-center gap-3 px-8 py-3.5 rounded-xl bg-amber-500 text-white font-black hover:bg-amber-600 transition-all shadow-xl shadow-amber-500/25 hover:scale-[1.02] active:scale-[0.98] uppercase text-[10px] tracking-[0.2em]"
                            >
                                <span className="material-symbols-outlined text-[20px]">alternate_email</span>
                                {order.status === 'Brouillon' ? 'Envoyer' : 'Renvoyer'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="flex items-center gap-3 px-10 py-3.5 rounded-xl bg-primary text-white font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/25 hover:scale-[1.02] active:scale-[0.98] uppercase text-[10px] tracking-[0.2em]"
                        >
                            <span className="material-symbols-outlined text-[20px]">verified</span>
                            {order?.status === 'Envoyée' ? 'Mettre à jour' : 'Valider'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Email Input Modal Overlay Premium */}
            {isEmailModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">
                        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                            <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-[0.2em] text-[10px] flex items-center gap-3">
                                <div className="size-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-amber-500 text-[18px]">alternate_email</span>
                                </div>
                                Validation & Envoi
                            </h4>
                            <button onClick={() => setIsEmailModalOpen(false)} className="text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10">
                                <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                    Veuillez confirmer ou modifier l'adresse email de destination pour le fournisseur <span className="font-black text-primary uppercase">{supplier?.name}</span>.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Adresse Email Destination</label>
                                <div className="relative group">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-colors">
                                        <span className="material-symbols-outlined text-[18px]">mail</span>
                                    </div>
                                    <input
                                        type="email"
                                        required
                                        className="w-full h-12 pl-14 pr-4 bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-xl outline-none focus:border-primary transition-all text-sm font-bold"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="destinataire@fournisseur.com"
                                        autoFocus
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-8 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex gap-4">
                            <button
                                onClick={() => setIsEmailModalOpen(false)}
                                className="flex-1 h-12 rounded-xl text-slate-500 font-black hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-[10px] uppercase tracking-[0.2em]"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSendEmail}
                                className="flex-1 h-12 rounded-xl bg-primary text-white font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-3"
                            >
                                <span className="material-symbols-outlined text-[20px]">send</span>
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseOrderModal;
